/**
 * Irrigation Program Redux Slice
 *
 * Manages state for irrigation programs including CRUD operations,
 * status transitions, and step management.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import programApi from '@/services/programApi';
import type {
  Program,
  ProgramSummary,
  ProgramListResponse,
  ProgramCreateRequest,
  ProgramUpdateRequest,
  StatusUpdateRequest,
  StepCreateRequest,
  StepUpdateRequest,
  ExecutionRun,
  ExecutionStrategy,
  ActiveExecutionsResponse,
  ManualRunMode,
} from '@/services/programApi';

// ============================================================================
// State Interface
// ============================================================================

interface ProgramState {
  programs: ProgramSummary[];
  currentProgram: Program | null;
  total: number;
  page: number;
  pages: number;
  loading: boolean;
  saving: boolean;
  error: string | null;
  // Execution tracking
  activeRuns: Record<number, ExecutionRun>;  // keyed by program_id
  executionLoading: boolean;
}

const initialState: ProgramState = {
  programs: [],
  currentProgram: null,
  total: 0,
  page: 1,
  pages: 0,
  loading: false,
  saving: false,
  error: null,
  activeRuns: {},
  executionLoading: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchPrograms = createAsyncThunk(
  'programs/fetchPrograms',
  async ({ farmId, params }: { farmId: string; params?: { skip?: number; limit?: number; status?: string; zone_id?: number; has_fertigation?: boolean } }) => {
    return await programApi.listPrograms(farmId, params);
  }
);

export const fetchProgram = createAsyncThunk(
  'programs/fetchProgram',
  async (programId: number) => {
    return await programApi.getProgram(programId);
  }
);

export const createProgram = createAsyncThunk(
  'programs/createProgram',
  async (data: ProgramCreateRequest, { rejectWithValue }) => {
    try {
      return await programApi.createProgram(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create program';
      return rejectWithValue(message);
    }
  }
);

export const updateProgram = createAsyncThunk(
  'programs/updateProgram',
  async ({ programId, data }: { programId: number; data: ProgramUpdateRequest }, { rejectWithValue }) => {
    try {
      return await programApi.updateProgram(programId, data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update program';
      return rejectWithValue(message);
    }
  }
);

export const deleteProgram = createAsyncThunk(
  'programs/deleteProgram',
  async (programId: number, { rejectWithValue }) => {
    try {
      await programApi.deleteProgram(programId);
      return programId;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete program';
      return rejectWithValue(message);
    }
  }
);

export const updateProgramStatus = createAsyncThunk(
  'programs/updateStatus',
  async ({ programId, data }: { programId: number; data: StatusUpdateRequest }, { rejectWithValue }) => {
    try {
      return await programApi.updateProgramStatus(programId, data);
    } catch (error: unknown) {
      // A 409 carries the structured activation verdict —
      // {message, critical: [...], warnings: [...]} — and that payload IS the
      // answer the operator needs: which programs clash, over how many valves
      // and dates. Flattening it to a string here is what made the conflict
      // dialog dead code: the page's catch looked for `critical` in something
      // that was already just text, and the user got "no se pudo actualizar"
      // with nothing to act on (Los Mochos, programa 52, 2026-08-14).
      //
      // Same shape as triggerProgramExecution: reject with the object so a local
      // catch can open a dialog. Note the API client throws a PLAIN object, not
      // an Error, so `instanceof Error` is false — that is why the old branch
      // also lost the server's message and always said "Failed to update status".
      const status = (error as { status?: number })?.status;
      const details = (error as { details?: unknown })?.details;
      if (status === 409 && details && typeof details === 'object') {
        return rejectWithValue(details);
      }
      const message = (error as { message?: string })?.message
        || (error instanceof Error ? error.message : 'Failed to update status');
      return rejectWithValue(message);
    }
  }
);

export const addStep = createAsyncThunk(
  'programs/addStep',
  async ({ programId, data }: { programId: number; data: StepCreateRequest }, { rejectWithValue }) => {
    try {
      return await programApi.addStep(programId, data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add step';
      return rejectWithValue(message);
    }
  }
);

export const updateStep = createAsyncThunk(
  'programs/updateStep',
  async ({ stepId, data }: { stepId: number; data: StepUpdateRequest }, { rejectWithValue }) => {
    try {
      return await programApi.updateStep(stepId, data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update step';
      return rejectWithValue(message);
    }
  }
);

export const deleteStep = createAsyncThunk(
  'programs/deleteStep',
  async (stepId: number, { rejectWithValue }) => {
    try {
      await programApi.deleteStep(stepId);
      return stepId;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete step';
      return rejectWithValue(message);
    }
  }
);

export const reorderSteps = createAsyncThunk(
  'programs/reorderSteps',
  async ({ programId, stepIds }: { programId: number; stepIds: number[] }, { rejectWithValue }) => {
    try {
      return await programApi.reorderSteps(programId, stepIds);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to reorder steps';
      return rejectWithValue(message);
    }
  }
);

// ============================================================================
// Execution Thunks
// ============================================================================

export const triggerProgramExecution = createAsyncThunk(
  'programs/triggerExecution',
  async (
    {
      programId,
      executionDate,
      confirmOverlap,
      confirmOfflineSkip,
      mode,
    }: {
      programId: number;
      executionDate?: string;
      confirmOverlap?: boolean;
      confirmOfflineSkip?: boolean;
      mode?: ManualRunMode;
    },
    { rejectWithValue },
  ) => {
    try {
      return await programApi.triggerExecution(
        programId,
        executionDate,
        confirmOverlap === true,
        confirmOfflineSkip === true,
        mode,
      );
    } catch (error: unknown) {
      // Surface overlap / offline-pre-flight detail so the page can show a
      // confirmation dialog. PR-UX4 added `offline_valves` to the same
      // payload shape — accept that as a valid 409 detail too.
      const status = (error as { status?: number })?.status;
      const details = (error as { details?: unknown })?.details;
      if (
        status === 409
        && details
        && typeof details === 'object'
        && (
          'hard_conflicts' in details
          || 'warnings' in details
          || 'offline_valves' in details
        )
      ) {
        return rejectWithValue(details);
      }
      const message = error instanceof Error ? error.message : 'Failed to trigger execution';
      return rejectWithValue(message);
    }
  }
);

export const abortProgramExecution = createAsyncThunk(
  'programs/abortExecution',
  async (runId: number, { rejectWithValue }) => {
    try {
      return await programApi.abortExecution(runId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to abort execution';
      return rejectWithValue(message);
    }
  }
);

export const fetchActiveExecutions = createAsyncThunk(
  'programs/fetchActiveExecutions',
  async (farmId: string) => {
    return await programApi.getActiveExecutions(farmId);
  }
);

// ============================================================================
// Slice
// ============================================================================

const programSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {
    clearCurrentProgram(state) {
      state.currentProgram = null;
    },
    clearError(state) {
      state.error = null;
    },
    updateExecutionProgress(state, action) {
      const msg = action.payload as ExecutionRun;
      if (msg.program_id) {
        state.activeRuns[msg.program_id] = {
          ...state.activeRuns[msg.program_id],
          ...msg,
        };
      }
    },
    removeActiveRun(state, action) {
      const programId = action.payload as number;
      delete state.activeRuns[programId];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPrograms
      .addCase(fetchPrograms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrograms.fulfilled, (state, action) => {
        state.loading = false;
        state.programs = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pages = action.payload.pages;
      })
      .addCase(fetchPrograms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch programs';
      })

      // fetchProgram
      .addCase(fetchProgram.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgram.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProgram = action.payload;
      })
      .addCase(fetchProgram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch program';
      })

      // createProgram
      .addCase(createProgram.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createProgram.fulfilled, (state, action) => {
        state.saving = false;
        state.currentProgram = action.payload;
        // Push the new program into the list immediately so the card shows
        // up without waiting for the next fetchPrograms refresh. We project
        // the full Program shape down to ProgramSummary so it matches the
        // existing list items.
        const program = action.payload;
        const steps = program.steps ?? [];
        const totalDurationMinutes = program.execution_mode === 'parallel'
          ? steps.reduce((max, s) => Math.max(max, s.duration_minutes), 0)
          : steps.reduce(
              (sum, s, i) => sum + s.duration_minutes
                + (i < steps.length - 1 ? s.delay_after_minutes : 0),
              0,
            );
        const summary: ProgramSummary = {
          id: program.id,
          farm_id: program.farm_id,
          name: program.name,
          execution_mode: program.execution_mode,
          execution_strategy: program.execution_strategy,
          adjustment_mode: program.adjustment_mode,
          pump_station_id: program.pump_station_id ?? null,
          status: program.status,
          is_enabled: program.is_enabled,
          recurrence_type: program.recurrence_type,
          recurrence_days: program.recurrence_days,
          start_time: program.start_time,
          start_date: program.start_date,
          end_date: program.end_date,
          zone_cycling_enabled: program.zone_cycling_enabled,
          step_count: steps.length,
          total_duration_minutes: totalDurationMinutes,
          created_at: program.created_at,
          updated_at: program.updated_at,
        };
        // Avoid duplicates if the same program is somehow already in state.
        const existingIdx = state.programs.findIndex(p => p.id === summary.id);
        if (existingIdx === -1) {
          state.programs.unshift(summary);
          state.total += 1;
        } else {
          state.programs[existingIdx] = summary;
        }
      })
      .addCase(createProgram.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Failed to create program';
      })

      // updateProgram
      .addCase(updateProgram.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateProgram.fulfilled, (state, action) => {
        state.saving = false;
        state.currentProgram = action.payload;
      })
      .addCase(updateProgram.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Failed to update program';
      })

      // deleteProgram
      .addCase(deleteProgram.fulfilled, (state, action) => {
        state.programs = state.programs.filter(p => p.id !== action.payload);
        if (state.currentProgram?.id === action.payload) {
          state.currentProgram = null;
        }
      })

      // updateProgramStatus
      .addCase(updateProgramStatus.fulfilled, (state, action) => {
        state.currentProgram = action.payload;
        const idx = state.programs.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) {
          state.programs[idx] = {
            ...state.programs[idx],
            status: action.payload.status,
            is_enabled: action.payload.is_enabled,
          };
        }
        // If the program wasn't in the list yet, the next fetchPrograms
        // call from the caller will add it — we intentionally don't fabricate
        // a ProgramSummary here because updateProgramStatus's payload doesn't
        // always include the full step list.
      })

      // addStep - refresh current program
      .addCase(updateProgramStatus.rejected, (state, action) => {
        // state.error is typed string | null and is rendered directly, so the
        // structured 409 payload must NOT land here — extract `.message`. The
        // whole object still reaches the page through `.unwrap()`, which is
        // where the conflict dialog is opened.
        const payload = action.payload;
        if (typeof payload === 'string') {
          state.error = payload;
        } else if (payload && typeof payload === 'object' && 'message' in payload) {
          state.error = String((payload as { message: unknown }).message);
        } else {
          state.error = 'Failed to update status';
        }
      })
      .addCase(addStep.fulfilled, (state, action) => {
        if (state.currentProgram?.steps) {
          state.currentProgram.steps.push(action.payload);
          state.currentProgram.steps.sort((a, b) => a.step_order - b.step_order);
        }
      })

      // updateStep
      .addCase(updateStep.fulfilled, (state, action) => {
        if (state.currentProgram?.steps) {
          const idx = state.currentProgram.steps.findIndex(s => s.id === action.payload.id);
          if (idx !== -1) {
            state.currentProgram.steps[idx] = action.payload;
          }
        }
      })

      // deleteStep
      .addCase(deleteStep.fulfilled, (state, action) => {
        if (state.currentProgram?.steps) {
          state.currentProgram.steps = state.currentProgram.steps.filter(s => s.id !== action.payload);
        }
      })

      // reorderSteps
      .addCase(reorderSteps.fulfilled, (state, action) => {
        if (state.currentProgram) {
          state.currentProgram.steps = action.payload;
        }
      })

      // triggerProgramExecution
      .addCase(triggerProgramExecution.pending, (state) => {
        state.executionLoading = true;
      })
      .addCase(triggerProgramExecution.fulfilled, (state, action) => {
        state.executionLoading = false;
        if (action.payload.program_id) {
          state.activeRuns[action.payload.program_id] = action.payload;
        }
      })
      .addCase(triggerProgramExecution.rejected, (state, action) => {
        state.executionLoading = false;
        // The thunk may reject with EITHER a string (legacy errors) OR a
        // structured pre-flight payload {message, hard_conflicts, warnings,
        // offline_valves, ...}. state.error is typed string | null so any
        // component reading it expects a string — extract `.message` from
        // the object so we don't crash React with an object child.
        // The full structured payload is still available to runProgramNow's
        // local catch via `.unwrap()` — that's where the dialog gets opened.
        const payload = action.payload;
        if (typeof payload === 'string') {
          state.error = payload;
        } else if (payload && typeof payload === 'object' && 'message' in payload) {
          state.error = String((payload as { message: unknown }).message);
        } else {
          state.error = 'Failed to trigger execution';
        }
      })

      // abortProgramExecution
      .addCase(abortProgramExecution.fulfilled, (state, action) => {
        if (action.payload.program_id) {
          state.activeRuns[action.payload.program_id] = action.payload;
        }
      })

      // fetchActiveExecutions
      .addCase(fetchActiveExecutions.fulfilled, (state, action) => {
        state.activeRuns = {};
        for (const exec of action.payload.executions) {
          state.activeRuns[exec.program_id] = exec;
        }
      });
  },
});

// ============================================================================
// Selectors
// ============================================================================

export const selectPrograms = (state: RootState) => state.programs.programs;
export const selectCurrentProgram = (state: RootState) => state.programs.currentProgram;
export const selectProgramsLoading = (state: RootState) => state.programs.loading;
export const selectProgramsSaving = (state: RootState) => state.programs.saving;
export const selectProgramsError = (state: RootState) => state.programs.error;
export const selectProgramsPagination = (state: RootState) => ({
  total: state.programs.total,
  page: state.programs.page,
  pages: state.programs.pages,
});
export const selectActiveRuns = (state: RootState) => state.programs.activeRuns;
export const selectActiveRunForProgram = (programId: number) =>
  (state: RootState) => state.programs.activeRuns[programId] ?? null;
export const selectExecutionLoading = (state: RootState) => state.programs.executionLoading;

// ============================================================================
// Exports
// ============================================================================

export const { clearCurrentProgram, clearError, updateExecutionProgress, removeActiveRun } = programSlice.actions;
export const programReducer = programSlice.reducer;
export default programSlice.reducer;
